import { DEFAULT_ORDER, GAMES } from "../../shared/curriculum/games";
import type { GameContent, GameId } from "../../shared/curriculum";
import type { Localized } from "../../shared/i18n";
import { gameCardArt } from "./assets";
import type { SkillKey, StepCopy } from "./copy";

/**
 * The five games, as the website shows them.
 *
 * 🔴 THE ORDER, THE COUNT AND THE LEVELS COME FROM `GAMES`, NOT FROM A LIST
 * TYPED HERE. `GAMES` is a fixed five-tuple, so a sixth game does not compile
 * and a renamed one cannot silently vanish from the website. A game page's
 * three levels — name, objective, NCF-FS codes — are read straight off the
 * registry, so the website can never advertise a level the product does not
 * have. Only what a registry has no opinion about — the display name, the art,
 * the marketing sentences — is attached below.
 *
 * ⚠️ THE DISPLAY NAMES AND THE HOW-TO-PLAY LINES ARE TRANSCRIBED, NOT IMPORTED,
 * AND THAT IS THE LAYERING RULE RATHER THAN AN OVERSIGHT. ENGINEERING-GUIDE.md
 * §5: features never import each other's internals. The names match
 * `games-spec.md` §2 (OD-7 still open); the steps and the grown-ups' notes match
 * `features/games/shared/HowToPlay.tsx`'s `GUIDES`. If either changes, this is
 * the file to change with it.
 */

export interface MarketingGame {
  id: GameId;
  /** The registry entry, so levels, skills and exertion cannot drift. */
  content: GameContent;
  title: string;
  /** The game's painted plate, compressed for a card (`public/landingpage/games`). */
  image: string;
  /** The content skill, keyed to its `--color-skill-*` token. */
  skill: SkillKey;
  /**
   * The skill colour, as Tailwind classes rather than computed strings.
   *
   * 🔴 STATIC CLASS NAMES, BECAUSE TAILWIND READS SOURCE TEXT AND NOT RUNTIME
   * VALUES. `bg-skill-${skill}` compiles to nothing at all.
   */
  dotClass: string;
  tintClass: string;
  ringClass: string;
  /**
   * Is this one playable by a stranger, right now, with no account.
   *
   * Exactly one is: Sky Quest, whose free round lives at `#/sky-quest/play`
   * (`FreePlayApp`). The rest need a school — a roster, a class, a signed-in
   * child — so their call to action is Connect.
   *
   * ⚠️ IT DESCRIBES THE WEBSITE'S OFFER, NOT AN ENTITLEMENT. The free round is
   * public because the router says so; the others are behind a session the
   * server enforces. Flipping this changes what a button links to, nothing else.
   */
  free: boolean;
  /** Does this game have its own page at `#/<id>`. See `GAME_PAGE_IDS`. */
  hasPage: boolean;
}

/**
 * The games with a page of their own, in the order the website lists them.
 *
 * 🔴 IT IS THE DOCUMENTED DEFAULT ORDER — `games-spec.md` §2, *"Sky Quest →
 * Turo's World → Letter Lagoon → Bridge Patterns"* — and it has four games in
 * it. Kalqy World is out of that order (OD-46) and stays reachable from the
 * product's library; on the website it is a card that leads to Connect rather
 * than to a page of its own.
 */
export const GAME_PAGE_IDS: readonly GameId[] = DEFAULT_ORDER;

const DETAIL: Record<
  GameId,
  Pick<MarketingGame, "title" | "skill" | "dotClass" | "tintClass" | "ringClass" | "free">
> = {
  "sky-quest": {
    title: "Sky Quest",
    skill: "numeracy",
    dotClass: "bg-skill-numeracy",
    tintClass: "bg-skill-numeracy/12",
    ringClass: "group-hover:border-skill-numeracy/50",
    free: true,
  },
  "letter-lagoon": {
    title: "Letter Lagoon",
    skill: "literacy",
    dotClass: "bg-skill-literacy",
    tintClass: "bg-skill-literacy/12",
    ringClass: "group-hover:border-skill-literacy/50",
    free: false,
  },
  "turos-world": {
    title: "Turo’s World",
    skill: "generalAwareness",
    dotClass: "bg-skill-general-awareness",
    tintClass: "bg-skill-general-awareness/12",
    ringClass: "group-hover:border-skill-general-awareness/50",
    free: false,
  },
  "kalqy-world": {
    title: "Kalqy World",
    skill: "socialAwareness",
    dotClass: "bg-skill-social-awareness",
    tintClass: "bg-skill-social-awareness/12",
    ringClass: "group-hover:border-skill-social-awareness/50",
    free: false,
  },
  "bridge-patterns": {
    title: "Bridge Patterns",
    skill: "patterns",
    dotClass: "bg-skill-matching",
    tintClass: "bg-skill-matching/12",
    ringClass: "group-hover:border-skill-matching/50",
    free: false,
  },
};

export const MARKETING_GAMES: readonly MarketingGame[] = GAMES.map((content) => ({
  id: content.id,
  content,
  image: gameCardArt(content.id),
  hasPage: GAME_PAGE_IDS.includes(content.id),
  ...DETAIL[content.id],
}));

export function marketingGame(id: GameId): MarketingGame {
  const game = MARKETING_GAMES.find((g) => g.id === id);
  // Every GameId is in the five-tuple, so this is a typo at build time, not a runtime case.
  if (!game) throw new Error(`No marketing entry for game "${id}"`);
  return game;
}

/* ── The game pages ──────────────────────────────────────────────────────── */

/** The movements a page draws an icon for. Each maps to one icon in `icons.tsx`. */
export type MoveIcon =
  | "count"
  | "lean"
  | "hold"
  | "jump"
  | "duck"
  | "listen"
  | "step"
  | "centre"
  | "watch"
  | "pose"
  | "wait"
  | "run"
  | "point"
  | "grab"
  | "drop";

/** What only a game page says about a game — the words, and the icons beside them. */
export interface GamePageCopy {
  /** The hero's sentence: what a child does and what they learn. */
  blurb: string;
  /** One line that says the whole loop — `GUIDES[id].summary`, transcribed. */
  summary: string;
  steps: [StepCopy, StepCopy, StepCopy];
  /** The grown-ups' note — `GUIDES[id].adult`, transcribed. */
  adult: string;
  /** The three movements, short enough for a tile. */
  moves: [string, string, string];
  /**
   * Each level card's "move" line. Sky Quest L1 asks for no gross-motor act
   * (`motorAction: null` — a 3-year-old's first thirty seconds), and its line
   * says so rather than inventing a movement.
   */
  levelMoves: [string, string, string];
}

/** The non-verbal half of a game page: which icon sits beside which line. */
export interface GamePageMeta {
  stepIcons: [MoveIcon, MoveIcon, MoveIcon];
  moveIcons: [MoveIcon, MoveIcon, MoveIcon];
  /** The content skill first, then the motor skills the moves build. */
  skills: SkillKey[];
}

export const GAME_PAGE_META: Record<GameId, GamePageMeta> = {
  "sky-quest": {
    stepIcons: ["count", "lean", "hold"],
    moveIcons: ["lean", "hold", "jump"],
    skills: ["numeracy", "coordination", "bodyAwareness"],
  },
  "turos-world": {
    stepIcons: ["watch", "pose", "wait"],
    moveIcons: ["pose", "point", "jump"],
    skills: ["generalAwareness", "bodyAwareness", "coordination"],
  },
  "letter-lagoon": {
    stepIcons: ["listen", "step", "centre"],
    moveIcons: ["step", "hold", "centre"],
    skills: ["literacy", "balance", "coordination"],
  },
  "bridge-patterns": {
    stepIcons: ["point", "grab", "drop"],
    moveIcons: ["point", "grab", "drop"],
    skills: ["patterns", "coordination"],
  },
  "kalqy-world": {
    stepIcons: ["run", "lean", "jump"],
    moveIcons: ["lean", "jump", "duck"],
    skills: ["socialAwareness", "coordination", "bodyAwareness"],
  },
};

const EN_PAGES: Record<GameId, GamePageCopy> = {
  "sky-quest": {
    blurb:
      "Count what is on the board, then move your whole body to the island with the right number. Later levels add and take away — with a jump.",
    summary: "Find the right answer, move to it, and stay there to choose it.",
    steps: [
      { title: "Find the right answer", body: "Count what you see on the board." },
      { title: "Move to the answer", body: "Step or lean to reach the island with that number." },
      { title: "Stay there to choose it", body: "Hold still on the island and it is chosen." },
    ],
    adult:
      "Three islands, one right answer. Stepping or leaning moves between them — holding one is what chooses it.",
    moves: ["Step or lean", "Hold still", "Jump (levels 2–3)"],
    levelMoves: ["Gentle start · no jumping", "Move: jump", "Move: jump"],
  },
  "turos-world": {
    blurb:
      "Turo shows a pose and children copy it beside him, then learn to say hello and the kind words for every part of the day.",
    summary: "Watch Turo show a pose, copy it beside him, and hold it until it moves on.",
    steps: [
      { title: "Watch Turo", body: "Turo shows a pose and says what it means." },
      { title: "Copy the pose", body: "Stand beside him and do the same." },
      { title: "Hold it", body: "Every pose moves on by itself after a few seconds." },
    ],
    adult:
      "Turo shows a pose and you copy it beside him. Nothing here can be got wrong — every pose moves on by itself after a few seconds.",
    moves: ["Copy a pose", "Hold it still", "Greet with Turo"],
    levelMoves: ["Move: copy and hold", "Move: greet with Turo", "Move: kind words"],
  },
  "letter-lagoon": {
    blurb:
      "Hear a letter or a sound, then step onto the stone that matches it. Letters, sounds and what comes next — every one spoken aloud.",
    summary: "Listen to the letter, jump onto the stone with that letter, and return to the middle.",
    steps: [
      { title: "Hear the letter", body: "Every letter and sound is spoken aloud." },
      { title: "Step onto the stone", body: "Step onto the stone with the letter you heard." },
      { title: "Back to the middle", body: "Return to the centre and the next one begins." },
    ],
    adult:
      "Step onto the stone with the letter you heard and hold it there. Standing up with the camera on, the next question waits until you are back in the middle.",
    moves: ["Step onto a stone", "Hold it there", "Back to the centre"],
    levelMoves: ["Move: step", "Move: step", "Move: step"],
  },
  "bridge-patterns": {
    blurb:
      "Finish the pattern on the bridge. Grab the missing piece with your hand, carry it across and drop it into the gap.",
    summary: "Grab the missing picture with your fist, carry it to the hole, and open your hand.",
    steps: [
      { title: "Point at a picture", body: "Hold up one open hand to move the pointer." },
      { title: "Close your hand to grab", body: "Make a fist on the piece that fits the pattern." },
      { title: "Open it over the hole", body: "Carry it to the gap — the ring turns green — and let go." },
    ],
    adult:
      "Hold one open hand up to move the pointer. Close it into a fist on the picture you want, carry it across to the hole in the pattern, then open your hand to drop it in. If a fist is not being read, pointing and holding still also works.",
    moves: ["Open hand to point", "Make a fist to grab", "Open to drop"],
    levelMoves: ["Move: grab & place", "Move: grab & place", "Move: grab & place"],
  },
  "kalqy-world": {
    blurb:
      "Run with Kalqy through three worlds. At each gate, find the thing Kalqy names — a hat, a cow, a red balloon — then jump and duck on the way.",
    summary: "Run along the path, step in front of the right answer and point at it, and jump over what is in the way.",
    steps: [
      { title: "Run with Kalqy", body: "Kalqy runs along the path by himself." },
      { title: "Step and point", body: "Step in front of the right card and point at it until it fills." },
      { title: "Jump and duck", body: "Jump over what is on the path, duck under what is above it." },
    ],
    adult:
      "Kalqy runs by himself. At a gate, step in front of the card with the right answer and point at it until it fills; jump over what is on the path and duck under what is above it.",
    moves: ["Step and point", "Jump over", "Duck under"],
    levelMoves: ["Move: point · jump · duck", "Move: point · jump · duck", "Move: point · jump · duck"],
  },
};

export const GAME_PAGE_COPY: Localized<Record<GameId, GamePageCopy>> = { en: EN_PAGES };
